// Scheduler Admin API service - connects to the auth-api microservice for scheduler admin functions
const AUTH_API_BASE =
  import.meta.env.VITE_AUTH_API_URL || "http://localhost:8081";

// Generate a correlation ID for request tracing
function generateCorrelationId(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 11)}`;
}

interface ApiError {
  error: string;
}

async function schedulerRequest<T>(
  endpoint: string,
  options: RequestInit = {},
): Promise<T> {
  const authData = localStorage.getItem("tradecraft-auth");
  const token = authData ? JSON.parse(authData).state?.token : null;

  const correlationId = generateCorrelationId();

  const headers: HeadersInit = {
    "Content-Type": "application/json",
    "X-Correlation-ID": correlationId,
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers,
  };

  const response = await fetch(`${AUTH_API_BASE}${endpoint}`, {
    ...options,
    headers,
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error((data as ApiError).error || "Request failed");
  }

  return data as T;
}

// Types
export type JobType = "sync" | "execution" | "snapshot" | "price_refresh_hourly" | "price_refresh_pretrade" | "price_refresh_afterhours" | "cache_compare";
export type RunStatus = "running" | "completed" | "failed";
export type TriggerType = "scheduled" | "manual";
export type StaleDataPolicy = "block" | "warn" | "ignore";

export interface SchedulerConfig {
  sync_cron: string;
  execution_cron: string;
  snapshot_cron: string;
  enabled: boolean;
  dry_run: boolean;
  stale_data_policy: StaleDataPolicy;
  timezone: string;
  updated_at?: string;
}

export interface SchedulerConfigUpdate {
  sync_cron?: string;
  execution_cron?: string;
  snapshot_cron?: string;
  enabled?: boolean;
  dry_run?: boolean;
  stale_data_policy?: StaleDataPolicy;
}

export interface RunningJob {
  id: string;
  job_type: JobType;
  trigger_type: TriggerType;
  correlation_id: string;
  started_at: string;
}

export interface SchedulerStatus {
  running: boolean;
  enabled: boolean;
  dry_run: boolean;
  timezone: string;
  sync_cron: string;
  execution_cron: string;
  snapshot_cron: string;
  next_sync_time: string;
  next_execution_time: string;
  next_snapshot_time: string;
  last_sync_run: string;
  last_execution_run: string;
  last_snapshot_run: string;
  execution_timeout: string;
  snapshot_timeout: string;
  sync_timeout: string;
  current_run_id?: string;
  current_job_type?: string;
  running_jobs: RunningJob[];
  running_jobs_count: number;
}

export interface SchedulerRun {
  id: string;
  job_type: JobType;
  trigger_type: TriggerType;
  correlation_id: string;
  status: RunStatus;
  started_at: string;
  completed_at?: string;
  duration_ms?: number;
  error_message?: string;
  errors?: string[];
  summary?: Record<string, unknown>;
  created_at: string;
}

export interface SchedulerRunsListResponse {
  runs: SchedulerRun[];
  total_count: number;
  page: number;
  limit: number;
  total_pages: number;
}

export interface SchedulerRunsFilter {
  page?: number;
  limit?: number;
  job_type?: JobType;
  status?: RunStatus;
}

export interface TriggerJobResponse {
  message: string;
  job_type: JobType;
  correlation_id: string;
  run_id?: string;
  result?: unknown;
}

export interface ReloadConfigResponse {
  message: string;
  next_sync_time: string;
  next_execution_time: string;
  next_snapshot_time: string;
}

export interface CleanupRunsResponse {
  message: string;
  deleted_count: number;
  retention_days: number;
}

// Execution Report types
export interface ExecutionLogEntry {
  id: string;
  investment_id: string;
  investment_name: string;
  strategy_name: string;
  user_email: string;
  brokerage_account_id: string | null;
  brokerage_account_name: string;
  status: string;
  error_message: string | null;
  execution_date: string;
  duration_ms: number | null;
  intended_trades: unknown;
  orders_executed: unknown;
  orders_failed: unknown;
  drift_detected: unknown;
  scale_factor: string;
  portfolio_value_before: number;
  cash_balance_before: number;
}

export interface ExecutionReportResponse {
  run: SchedulerRun;
  execution_logs: ExecutionLogEntry[];
  total_strategies: number;
  succeeded: number;
  failed: number;
  skipped: number;
}

// API Functions

/**
 * Get current scheduler configuration from database
 */
export async function getSchedulerConfig(): Promise<SchedulerConfig> {
  return schedulerRequest<SchedulerConfig>("/api/v1/admin/scheduler/config");
}

/**
 * Update scheduler configuration
 */
export async function updateSchedulerConfig(
  config: SchedulerConfigUpdate,
): Promise<{ message: string; warning?: string }> {
  return schedulerRequest("/api/v1/admin/scheduler/config", {
    method: "PUT",
    body: JSON.stringify(config),
  });
}

/**
 * Get current scheduler status with running jobs
 */
export async function getSchedulerStatus(): Promise<SchedulerStatus> {
  return schedulerRequest<SchedulerStatus>("/api/v1/admin/scheduler/status");
}

/**
 * Trigger a scheduler job manually
 */
export async function triggerSchedulerJob(
  jobType: JobType,
): Promise<TriggerJobResponse> {
  return schedulerRequest<TriggerJobResponse>(
    `/api/v1/admin/scheduler/trigger/${jobType}`,
    {
      method: "POST",
    },
  );
}

/**
 * Reload scheduler configuration from database
 */
export async function reloadSchedulerConfig(): Promise<ReloadConfigResponse> {
  return schedulerRequest<ReloadConfigResponse>(
    "/api/v1/admin/scheduler/reload-config",
    {
      method: "POST",
    },
  );
}

/**
 * Get paginated list of scheduler runs
 */
export async function listSchedulerRuns(
  filter: SchedulerRunsFilter = {},
): Promise<SchedulerRunsListResponse> {
  const params = new URLSearchParams();
  if (filter.page) params.append("page", String(filter.page));
  if (filter.limit) params.append("limit", String(filter.limit));
  if (filter.job_type) params.append("job_type", filter.job_type);
  if (filter.status) params.append("status", filter.status);

  const queryString = params.toString();
  const url = `/api/v1/admin/scheduler/runs${queryString ? `?${queryString}` : ""}`;
  return schedulerRequest<SchedulerRunsListResponse>(url);
}

/**
 * Get a specific scheduler run by ID
 */
export async function getSchedulerRun(runId: string): Promise<SchedulerRun> {
  return schedulerRequest<SchedulerRun>(
    `/api/v1/admin/scheduler/runs/${runId}`,
  );
}

/**
 * Get a scheduler run by correlation ID
 */
export async function getSchedulerRunByCorrelationId(
  correlationId: string,
): Promise<SchedulerRun> {
  return schedulerRequest<SchedulerRun>(
    `/api/v1/admin/scheduler/runs/correlation/${correlationId}`,
  );
}

/**
 * Get the latest run for a job type
 */
export async function getLatestSchedulerRun(
  jobType: JobType,
): Promise<SchedulerRun> {
  return schedulerRequest<SchedulerRun>(
    `/api/v1/admin/scheduler/runs/latest/${jobType}`,
  );
}

/**
 * Cleanup old scheduler runs
 */
export async function cleanupSchedulerRuns(
  retentionDays: number = 30,
): Promise<CleanupRunsResponse> {
  return schedulerRequest<CleanupRunsResponse>(
    `/api/v1/admin/scheduler/runs/cleanup?retention_days=${retentionDays}`,
    {
      method: "DELETE",
    },
  );
}

/**
 * Get the full execution report for a scheduler run (includes per-strategy execution logs)
 */
export async function getExecutionReport(
  runId: string,
): Promise<ExecutionReportResponse> {
  return schedulerRequest<ExecutionReportResponse>(
    `/api/v1/admin/scheduler/runs/${runId}/report`,
  );
}

// Utility functions

/**
 * Format cron expression into human-readable time (for standard ET schedules)
 */
export function formatCronToTime(cron: string): string {
  // Parse simple cron like "30 14 * * 1-5" -> "2:30 PM"
  const parts = cron.split(" ");
  if (parts.length < 5) return cron;

  const minute = parseInt(parts[0], 10);
  const hour = parseInt(parts[1], 10);

  if (isNaN(minute) || isNaN(hour)) return cron;

  const ampm = hour >= 12 ? "PM" : "AM";
  const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
  const displayMinute = minute.toString().padStart(2, "0");

  return `${displayHour}:${displayMinute} ${ampm}`;
}

/**
 * Convert time string to cron expression (weekdays only)
 */
export function timeToCron(time: string): string {
  // Parse "14:30" or "2:30 PM" format
  let hour: number;
  let minute: number;

  if (time.includes(":")) {
    const [h, rest] = time.split(":");
    hour = parseInt(h, 10);

    if (rest.toLowerCase().includes("pm") && hour !== 12) {
      hour += 12;
    } else if (rest.toLowerCase().includes("am") && hour === 12) {
      hour = 0;
    }

    minute = parseInt(rest, 10);
  } else {
    return ""; // Invalid format
  }

  // Return cron for weekdays (Mon-Fri)
  return `${minute} ${hour} * * 1-5`;
}

/**
 * Format duration in milliseconds to human-readable string
 */
export function formatDuration(ms: number | undefined): string {
  if (ms === undefined) return "-";
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  const minutes = Math.floor(ms / 60000);
  const seconds = ((ms % 60000) / 1000).toFixed(0);
  return `${minutes}m ${seconds}s`;
}

/**
 * Get status badge color based on run status
 */
export function getStatusColor(
  status: RunStatus,
): "green" | "yellow" | "red" | "gray" {
  switch (status) {
    case "completed":
      return "green";
    case "running":
      return "yellow";
    case "failed":
      return "red";
    default:
      return "gray";
  }
}

/**
 * Get job type display name
 */
export function getJobTypeDisplayName(jobType: JobType): string {
  switch (jobType) {
    case "sync":
      return "Portfolio Sync";
    case "execution":
      return "Trade Execution";
    case "snapshot":
      return "Position Snapshot";
    case "price_refresh_hourly":
      return "Price Refresh (Hourly)";
    case "price_refresh_pretrade":
      return "Price Refresh (Pre-Trade)";
    case "price_refresh_afterhours":
      return "Price Refresh (After Hours)";
    case "cache_compare":
      return "Cache Comparison";
    default:
      return jobType;
  }
}

/**
 * Get job type description
 */
export function getJobTypeDescription(jobType: JobType): string {
  switch (jobType) {
    case "sync":
      return "Syncs all portfolio positions with brokerage accounts";
    case "execution":
      return "Executes pending trades based on strategy allocations";
    case "snapshot":
      return "Takes a snapshot of all positions for performance tracking";
    case "price_refresh_hourly":
      return "Hourly yFinance price refresh for all cached tickers";
    case "price_refresh_pretrade":
      return "Final price refresh before 3:40 PM trade execution";
    case "price_refresh_afterhours":
      return "Captures closing prices after market close";
    case "cache_compare":
      return "Compares RAM cache vs Redis day-by-day for all tickers";
    default:
      return "";
  }
}

/**
 * Check if a job type is a price refresh type
 */
export function isPriceRefreshJobType(jobType: JobType): boolean {
  return jobType.startsWith("price_refresh_");
}
